import React from 'react';

import { Avatar } from 'antd';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PendingIcon from '@mui/icons-material/Pending';
import { Box, Chip, Typography } from '@mui/material';

import { RawEvent } from '../types/agendaTypes';
import Text from 'components/Text';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { useIsMobile } from 'utils/mediaQueries';

interface DetailsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmOccurrence: () => void;
  onConfirmSeries: () => void;
  event: any;
  correspondingEvent?: RawEvent;
  eventStatus?: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'PARTIAL';
  isPrivateMeeting?: boolean;
  onRedirectToAccessHub?: () => void;
  isRedirecting?: boolean;
}

const DetailsModal: React.FC<DetailsModalProps> = ({
  open,
  onClose,
  onConfirmOccurrence,
  onConfirmSeries: _onConfirmSeries,
  event,
  correspondingEvent,
  eventStatus,
  isPrivateMeeting = false,
  onRedirectToAccessHub,
  isRedirecting = false,
}) => {
  const isMobile = useIsMobile();

  const BulletPoint: React.FC = () => (
    <FiberManualRecordIcon
      color="primary"
      sx={{
        color: 'primary',
        fontSize: '0.75rem',
        marginRight: '0.25rem',
      }}
    />
  );

  const isEventAccepted = event.status === 'ACCEPTED' || event.status === 'PARTIAL';
  const hasRecurrence =
    Array.isArray(correspondingEvent?.schedule?.slots) &&
    correspondingEvent?.schedule?.slots?.length > 1;

  // Função para obter o status do usuário
  const getUserStatus = () => {
    const status = eventStatus || event.status;
    switch (status) {
      case 'ACCEPTED':
        return {
          label: 'Aceito',
          color: 'success' as const,
          icon: <CheckCircleIcon sx={{ fontSize: '1rem' }} />,
        };
      case 'PARTIAL':
        return {
          label: 'Aceito Parcialmente',
          color: 'success' as const,
          icon: <CheckCircleIcon sx={{ fontSize: '1rem' }} />,
        };
      case 'DECLINED':
        return {
          label: 'Recusado',
          color: 'error' as const,
          icon: <CancelIcon sx={{ fontSize: '1rem' }} />,
        };
      default:
        return {
          label: 'Pendente',
          color: 'default' as const,
          icon: <PendingIcon sx={{ fontSize: '1rem' }} />,
        };
    }
  };

  // Função para verificar se há palestrantes
  const hasPresenters =
    event.presenters && Array.isArray(event.presenters) && event.presenters.length > 0;

  // Função para verificar se há coordenador
  const hasCoordinator = event.coordinator && event.coordinator.firstName;

  const getDescription = () => {
    if (isPrivateMeeting) {
      return '';
    }
    if (isEventAccepted) return '';
    return hasRecurrence
      ? 'Ao confirmar este evento, você estará confirmando todas as ocorrências relacionadas.'
      : 'Confirme este evento único para prosseguir.';
  };

  const getConfirmButtonText = () => {
    if (isPrivateMeeting) return 'Acessar Hub de Reuniões';
    if (isEventAccepted && !isPrivateMeeting) return 'Fechar';
    return 'Confirmar evento';
  };

  const getCancelButtonText = () => {
    if (isPrivateMeeting) return 'Fechar';
    if (isEventAccepted && !isPrivateMeeting) return 'Fechar';
    return 'Fechar';
  };

  const handleConfirm = () => {
    if (isPrivateMeeting && onRedirectToAccessHub) {
      onRedirectToAccessHub();
    } else if (isEventAccepted && !isPrivateMeeting) {
      onClose();
    } else if (!isEventAccepted) {
      onConfirmOccurrence();
    }
  };

  const handleCancel = () => {
    if (isPrivateMeeting) {
      onClose();
    } else if (isEventAccepted) {
      onClose();
    } else {
      onClose();
    }
  };

  console.log('[DetailsModal] event', correspondingEvent);

  return (
    <GlobalModal
      open={open}
      title="DETALHES DO EVENTO"
      description={getDescription()}
      onClose={onClose}
      width={isMobile ? '95%' : '48rem'}
      height={isMobile ? '50%' : 'auto'}
      closeOnEscapeKeyDown
      closeOnBackdropClick
      onCancel={handleCancel}
      onConfirm={isEventAccepted && !isPrivateMeeting ? undefined : handleConfirm}
      confirmButtonText={getConfirmButtonText()}
      cancelButtonText={getCancelButtonText()}
      confirmDisabled={isRedirecting}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          gap: '2rem',
          paddingBottom: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            Dados do Evento
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            {event.title}
          </Typography>
          <Typography variant="subtitle1">{correspondingEvent?.description}</Typography>
          <Box
            sx={{
              display: 'flex',
              alignSelf: 'start',
              gap: 4,
            }}
          >
            {correspondingEvent?.type && (
              <Typography variant="subtitle1">
                <BulletPoint /> {correspondingEvent?.type.name}
              </Typography>
            )}
            {correspondingEvent?.is_exclusive && (
              <Typography>
                <BulletPoint /> Evento Exclusivo
              </Typography>
            )}
            {isPrivateMeeting && (
              <Typography>
                <BulletPoint /> Reunião Privada
              </Typography>
            )}
          </Box>
        </Box>

        {/* Seção de Status do Usuário */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Text size="medium" bold color="primary">
            Seu Status
          </Text>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={getUserStatus().icon}
              label={getUserStatus().label}
              color={getUserStatus().color}
              size="small"
              variant="outlined"
            />
            {getUserStatus().label === 'Pendente' && (
              <Text size="small" color="textSecondary">
                - Aguardando sua resposta
              </Text>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Text size="medium" bold color="primary">
            Local{' '}
          </Text>

          <Text size="medium">
            <BulletPoint />
            {event.location}
          </Text>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Text size="medium" bold color="primary">
            Agendamento
          </Text>
          <Box display="flex" gap="2rem" alignSelf="start">
            <Text size="medium" bold>
              {event.date} - {event.time}
            </Text>
            <Text size="medium">Duração: {event.duration}</Text>
          </Box>
          {event.scheduleRuleDescription === 'Evento único' ? (
            <>
              <Text color="primary">
                <BulletPoint /> Sem recorrencia.
              </Text>
            </>
          ) : (
            <>
              <Text color="primary">
                <BulletPoint /> Com recorrência:
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginLeft: '1.5rem',
                  gap: 0.5,
                }}
              >
                <Text size="medium">{event.scheduleRuleDescription}</Text>
                <Text size="medium">
                  {correspondingEvent?.schedule?.rule?.recurrence_end_rule ===
                  'AFTER_N_OCCURRENCES' ? (
                    `Finaliza após ${correspondingEvent?.schedule?.rule?.recurrence_rule_limit} ocorrências.`
                  ) : correspondingEvent?.schedule?.rule?.recurrence_end_rule === 'EXACT_DATE' ? (
                    <>
                      Finaliza em{' '}
                      <strong>
                        {correspondingEvent?.schedule?.rule?.recurrence_end_date
                          ?.split('-')
                          .reverse()
                          .join('/')}
                      </strong>
                    </>
                  ) : (
                    ''
                  )}
                </Text>
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            flexDirection: 'row',
            marginBottom: 2,
            gap: 4,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Coordenador
            </Typography>

            {hasCoordinator ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  gap: 1,
                  mt: 0.5,
                }}
              >
                <Avatar
                  size="small"
                  src={event.coordinator.photoUrl}
                  alt={event.coordinator.firstName}
                />
                <Text
                  size="medium"
                  truncate
                  tooltip={`${event.coordinator.firstName} ${event.coordinator.lastName}`}
                >
                  {event.coordinator.firstName} {event.coordinator.lastName}
                </Text>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 0.5,
                  padding: 1,
                }}
              >
                <Typography variant="subtitle1" color="textSecondary">
                  Nenhum coordenador cadastrado
                </Typography>
              </Box>
            )}
          </Box>

          {!isPrivateMeeting && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Palestrantes
              </Typography>

              {hasPresenters ? (
                Array.isArray(event.presenters) &&
                event.presenters.map((presenter: any) => (
                  <Box
                    key={presenter.uuid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'row',
                      gap: 1,
                    }}
                  >
                    <Avatar size="small" src={presenter.photoUrl} alt={presenter.firstName} />
                    <Text
                      size="medium"
                      truncate
                      tooltip={`${presenter.firstName} ${presenter.lastName}`}
                    >
                      {presenter.firstName} {presenter.lastName}
                    </Text>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: 1,
                  }}
                >
                  <Typography variant="subtitle1" color="textSecondary">
                    Nenhum palestrante cadastrado
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </GlobalModal>
  );
};

export default DetailsModal;
